/**
 * intakePermissions
 * ─────────────────
 * Feb 2026 — Phase 2 of B2 IntakeWizard extraction.
 *
 * Pure factory for the four permission helpers used by IntakeWizardDialog
 * (`hasPermission`, `canEditFieldCategory`, `isFieldVisible`,
 * `isFieldRequired`).  Mirrors the logic that lives inline in App.js at
 * lines 7020/7103/18296/18302 so legacy + V2 can share a single source
 * of truth.
 *
 * App.js is NOT modified by Phase 2 — it keeps its inline copies for
 * now.  Phase 3 will collapse the duplication.
 */
import { isSuperAdmin } from './helpers';
import {
  OFFICE_DEFAULT_PERMISSIONS,
  PROVIDER_DEFAULT_PERMISSIONS,
  SUPERVISOR_DEFAULT_PERMISSIONS,
  RECEPTION_DEFAULT_PERMISSIONS,
  INTERN_DEFAULT_PERMISSIONS,
} from '../constants';

export function createIntakePermissions({
  user,
  roles,
  userRoleName,
  intakeFormConfig,
}) {
  const hasPermission = (permission) => {
    if (!user) return false;
    if (isSuperAdmin(user)) return true;

    const userRole = (roles || []).find((r) => r.id === user.role_id);
    if (userRole) {
      return !!(userRole.permissions && userRole.permissions.includes(permission));
    }

    const roleName = userRoleName || (user?.role || '').toLowerCase();
    if (roleName === 'office' || roleName === 'office staff') {
      return OFFICE_DEFAULT_PERMISSIONS.includes(permission);
    }
    if (roleName === 'provider') return PROVIDER_DEFAULT_PERMISSIONS.includes(permission);
    if (roleName === 'supervisor') return SUPERVISOR_DEFAULT_PERMISSIONS.includes(permission);
    if (roleName === 'reception') return RECEPTION_DEFAULT_PERMISSIONS.includes(permission);
    if (roleName === 'intern') return INTERN_DEFAULT_PERMISSIONS.includes(permission);
    if (roleName === 'admin') return true;
    return false;
  };

  const isAdmin = () => !!(user && isSuperAdmin(user));

  const canEditFieldCategory = (category) => {
    if (!user) return false;
    if (isAdmin()) return true;
    if (category === 'status' && user?.is_waitlist_manager) return true;
    const permissionMap = {
      demographics: 'edit_client_demographics',
      clinical: 'edit_client_clinical',
      insurance: 'edit_client_insurance',
      status: 'edit_client_status',
      provider_assignment: 'manage_provider_assignments',
    };
    return hasPermission(permissionMap[category]);
  };

  const getFieldConfig = (fieldId) => {
    if (!intakeFormConfig || !intakeFormConfig.steps) return null;
    for (const step of intakeFormConfig.steps) {
      const field = (step.fields || []).find((f) => f.field_id === fieldId);
      if (field) return field;
    }
    return null;
  };

  const isFieldVisible = (fieldId) => {
    const f = getFieldConfig(fieldId);
    return f ? f.visible !== false : true;
  };

  const isFieldRequired = (fieldId) => {
    const f = getFieldConfig(fieldId);
    return f ? f.required === true : false;
  };

  return {
    hasPermission,
    isAdmin,
    canEditFieldCategory,
    getFieldConfig,
    isFieldVisible,
    isFieldRequired,
  };
}
